/**
 * Metric Trend Charts Component
 *
 * Displays 8 line charts visualizing demo data metrics over time:
 * - Net Sales, Check Count, FOH Hours, BOH Hours
 * - FOH Dollars, BOH Dollars, SPLH, Labor %
 *
 * Uses Chart.js with responsive grid layout
 * Adapts to view mode (shift-level shows date+shift, aggregate shows date only)
 */

import React, { useMemo } from 'react';
import { Box, Grid, Typography, Paper, Skeleton } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { format, parseISO } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * MetricTrendCharts Component
 * @param {Object} props - Component props
 * @param {Array} props.data - Data array (daily aggregates)
 * @param {boolean} props.loading - Loading state
 */
const MetricTrendCharts = ({ data, loading = false }) => {
  /**
   * Memoized chart options to prevent re-renders
   */
  const metricOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(124, 58, 237, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#7C3AED',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: (value) => Math.round(value).toLocaleString(),
          maxTicksLimit: 6
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  }), []);

  /**
   * Format data for charts
   * Charts ALWAYS show dates on X-axis with aggregated daily values
   * Regardless of view mode, data is aggregated by date
   * Memoized to avoid recalculation on every render
   */
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Group data by date (aggregate if shift-level data)
    const grouped = data.reduce((acc, record) => {
      const date = record.date;
      if (!acc[date]) {
        acc[date] = {
          date,
          netSales: 0,
          checkCount: 0,
          fohHours: 0,
          bohHours: 0,
          fohDollars: 0,
          bohDollars: 0,
          totalHours: 0,
          totalLaborDollars: 0,
          recordCount: 0
        };
      }

      // Sum values for the date
      acc[date].netSales += record.netSales || 0;
      acc[date].checkCount += record.checkCount || 0;
      acc[date].fohHours += record.fohHours || 0;
      acc[date].bohHours += record.bohHours || 0;
      acc[date].fohDollars += record.fohDollars || 0;
      acc[date].bohDollars += record.bohDollars || 0;
      acc[date].recordCount += 1;

      return acc;
    }, {});

    // Convert to array and sort by date
    const aggregated = Object.values(grouped).sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    // Calculate derived metrics for each date
    aggregated.forEach(day => {
      day.totalHours = day.fohHours + day.bohHours;
      day.totalLaborDollars = day.fohDollars + day.bohDollars;
      day.splh = day.totalHours > 0 ? day.netSales / day.totalHours : 0;
      day.laborPct = day.netSales > 0 ? (day.totalLaborDollars / day.netSales) * 100 : 0;
    });

    // Generate labels (dates only)
    const labels = aggregated.map(day => format(parseISO(day.date), 'MMM d'));

    // Helper function to determine smart scaling based on max value
    const getSmartScale = (values) => {
      const maxVal = Math.max(...values);
      if (maxVal >= 10000000) {
        // 10M+ -> show in millions
        return { divisor: 1000000, suffix: 'M', values: values.map(v => Math.round(v / 100000) / 10) };
      } else if (maxVal >= 1000000) {
        // 1M+ -> show in hundred thousands
        return { divisor: 100000, suffix: '00K', values: values.map(v => Math.round(v / 10000) / 10) };
      } else if (maxVal >= 100000) {
        // 100K+ -> show in ten thousands
        return { divisor: 10000, suffix: '0K', values: values.map(v => Math.round(v / 1000) / 10) };
      } else if (maxVal >= 10000) {
        // 10K+ -> show in thousands
        return { divisor: 1000, suffix: 'K', values: values.map(v => Math.round(v / 100) / 10) };
      } else if (maxVal >= 1000) {
        // 1K+ -> show in hundreds
        return { divisor: 100, suffix: '00s', values: values.map(v => Math.round(v / 10) / 10) };
      } else {
        // < 1K -> show as-is
        return { divisor: 1, suffix: '', values: values.map(v => Math.round(v * 10) / 10) };
      }
    };

    // Extract raw metric values
    const rawNetSales = aggregated.map(r => r.netSales);
    const rawCheckCount = aggregated.map(r => r.checkCount);
    const rawFohHours = aggregated.map(r => r.fohHours);
    const rawBohHours = aggregated.map(r => r.bohHours);
    const rawFohDollars = aggregated.map(r => r.fohDollars);
    const rawBohDollars = aggregated.map(r => r.bohDollars);
    const rawSplh = aggregated.map(r => r.splh);
    const rawLaborPct = aggregated.map(r => r.laborPct);

    // Apply smart scaling
    const netSalesScale = getSmartScale(rawNetSales);
    const checkCountScale = getSmartScale(rawCheckCount);
    const fohHoursScale = getSmartScale(rawFohHours);
    const bohHoursScale = getSmartScale(rawBohHours);
    const fohDollarsScale = getSmartScale(rawFohDollars);
    const bohDollarsScale = getSmartScale(rawBohDollars);
    // SPLH and Labor % don't need scaling
    const splhValues = rawSplh.map(v => Math.round(v * 10) / 10);
    const laborPctValues = rawLaborPct.map(v => Math.round(v * 10) / 10);

    return {
      labels,
      netSales: { values: netSalesScale.values, suffix: netSalesScale.suffix },
      checkCount: { values: checkCountScale.values, suffix: checkCountScale.suffix },
      fohHours: { values: fohHoursScale.values, suffix: fohHoursScale.suffix },
      bohHours: { values: bohHoursScale.values, suffix: bohHoursScale.suffix },
      fohDollars: { values: fohDollarsScale.values, suffix: fohDollarsScale.suffix },
      bohDollars: { values: bohDollarsScale.values, suffix: bohDollarsScale.suffix },
      splh: { values: splhValues, suffix: '' },
      laborPct: { values: laborPctValues, suffix: '' }
    };
  }, [data]);

  /**
   * Create dataset configuration for a metric
   */
  const createDataset = (metricValues, label) => ({
    labels: chartData?.labels || [],
    datasets: [
      {
        label,
        data: metricValues || [],
        borderColor: '#7C3AED',
        backgroundColor: 'rgba(124, 58, 237, 0.1)',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#7C3AED',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.3,
        fill: true
      }
    ]
  });

  /**
   * Build dynamic title based on metric and suffix
   */
  const buildTitle = (baseTitle, suffix, unit) => {
    if (!suffix) {
      return `${baseTitle}${unit ? ` (${unit})` : ''}`;
    }
    // For currency/units, combine unit symbol with suffix
    // e.g., '$' + 'M' = '$M', 'h' + 'K' = 'Kh'
    if (unit === '$') {
      return `${baseTitle} (${unit}${suffix})`;
    } else if (unit) {
      return `${baseTitle} (${suffix}${unit})`;
    } else {
      return `${baseTitle} (${suffix})`;
    }
  };

  /**
   * Render chart content based on loading/data state
   */
  const renderChartContent = (title, metricData) => {
    if (loading) {
      return <Skeleton variant="rectangular" height={200} />;
    }
    if (chartData && metricData?.values) {
      return (
        <Line
          data={createDataset(metricData.values, title)}
          options={metricOptions}
        />
      );
    }
    return (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED', mb: 2 }}>
        Metric Trends
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('Net Sales', chartData?.netSales?.suffix, '$')}
            </Typography>
            {renderChartContent(buildTitle('Net Sales', chartData?.netSales?.suffix, '$'), chartData?.netSales)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('Check Count', chartData?.checkCount?.suffix, '')}
            </Typography>
            {renderChartContent(buildTitle('Check Count', chartData?.checkCount?.suffix, ''), chartData?.checkCount)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('FOH Hours', chartData?.fohHours?.suffix, 'h')}
            </Typography>
            {renderChartContent(buildTitle('FOH Hours', chartData?.fohHours?.suffix, 'h'), chartData?.fohHours)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('BOH Hours', chartData?.bohHours?.suffix, 'h')}
            </Typography>
            {renderChartContent(buildTitle('BOH Hours', chartData?.bohHours?.suffix, 'h'), chartData?.bohHours)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('FOH Dollars', chartData?.fohDollars?.suffix, '$')}
            </Typography>
            {renderChartContent(buildTitle('FOH Dollars', chartData?.fohDollars?.suffix, '$'), chartData?.fohDollars)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              {buildTitle('BOH Dollars', chartData?.bohDollars?.suffix, '$')}
            </Typography>
            {renderChartContent(buildTitle('BOH Dollars', chartData?.bohDollars?.suffix, '$'), chartData?.bohDollars)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              SPLH ($)
            </Typography>
            {renderChartContent('SPLH ($)', chartData?.splh)}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#7C3AED', fontWeight: 600 }}>
              Labor %
            </Typography>
            {renderChartContent('Labor %', chartData?.laborPct)}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MetricTrendCharts;
